import { ClassicCard } from '@braze/web-sdk';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Image,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { useInboxModel } from '@common/models/InboxModel';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { UI } from 'src/apps/invest/common/shells/UI';
import { ROUTES } from 'src/apps/invest/config';
import { Loading } from 'src/apps/invest/property/views/common/Loading';

export const MessageDetails: React.FC = () => {
  const [message, setMessage] = useState<ClassicCard>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const inboxModel = useInboxModel();
  const navigate = useNavigate();
  const { id } = useParams();
  const cancelRef = React.useRef();
  const toast = useToast();

  useEffect(() => {
    if (!id) return;
    if (!inboxModel) return;
    const msg = inboxModel.getMessageById(id);
    if (!msg) return;
    setMessage(msg);
    inboxModel.markMessageAsViewed(msg);
  }, []);

  if (!message) return <Loading />;

  return (
    <UI>
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef as any} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Message?
            </AlertDialogHeader>
            <AlertDialogBody>Are you sure? This message will be permanently removed. </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef as any} onClick={onClose} variant={'ghost'} colorScheme="black">
                Cancel
              </Button>
              <Button
                colorScheme="black"
                variant="outline"
                onClick={() => {
                  toast({
                    duration: 3000,
                    isClosable: true,
                    position: 'top',
                    status: 'success',
                    title: 'Message Deleted',
                  });
                  onClose();
                  inboxModel.markMessageAsDismissed(message);
                  inboxModel.checkForMessages();
                  navigate(ROUTES.inbox);
                }}
                ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <VStack alignItems={'flex-start'} justifyContent={'flex-start'} gap="30px">
        <HStack gap="10px">
          <IconButton
            aria-label="Back to inbox"
            colorScheme="black"
            variant={'outline'}
            onClick={() => navigate(ROUTES.inbox)}
            size={'sm'}
            icon={<Icon icon="arrow-left-long" />}
          />
          <TextBlock as="h2" weight="bold" mb={'24px'} fontSize="40px">
            Inbox
          </TextBlock>
        </HStack>
        <Grid
          alignItems={'flex-start'}
          justifyContent={'flex-start'}
          borderWidth={'1px'}
          borderColor={'black'}
          borderRadius={'10px'}
          templateRows={'min-content 1fr'}
          templateColumns={'1fr 1fr'}
          w="100%">
          <GridItem
            alignItems={'center'}
            borderBottom="1px solid black"
            display="flex"
            flexDirection={'row'}
            gridColumn="1/3"
            gridRow="1/2"
            padding="10px 10px 10px 24px"
            justifyContent={'space-between'}>
            <TextBlock weight="bold" color={'black'}>
              {message?.title}
            </TextBlock>
            {message.url && (
              <Button
                onClick={() => {
                  inboxModel.markMessageAsClicked(message);
                  window.location.href = message.url as string;
                }}
                colorScheme="black"
                variant={'ghost'}
                rightIcon={<Icon icon="chevron-right" />}>
                {message.linkText ?? 'View'}
              </Button>
            )}
          </GridItem>
          <GridItem gridColumn="1/2" gridRow="2/3" padding="24px">
            <TextBlock>{message?.description}</TextBlock>
          </GridItem>
          {message?.imageUrl && (
            <GridItem gridColumn="2/3" gridRow="2/3" padding="24px">
              <Image src={message.imageUrl} w="100%" />
            </GridItem>
          )}
        </Grid>
        <HStack justifyContent={'center'} w="100%">
          <Button variant={'outline'} colorScheme="black" onClick={() => navigate(ROUTES.inbox)} ml={3}>
            Back To Inbox
          </Button>
          {message.dismissible && (
            <Button
              variant={'outline'}
              colorScheme="black"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOpen();
              }}
              ml={3}>
              Delete Message
            </Button>
          )}
        </HStack>
      </VStack>
    </UI>
  );
};
